import { useState } from 'react';
import { Loader2, Paperclip, CalendarDays, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { DatePickerDialog } from '@/shared/components/DatePickerDialog';
import { PLEA_TYPE_OPTIONS } from '../constants';
import { AttachmentList } from './AttachmentList';
import type { Attachment, PleaFormData } from '../types';

interface PleaFormSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  form: PleaFormData;
  onChange: (updater: (prev: PleaFormData) => PleaFormData) => void;
  onSubmit: () => void;
  submitting: boolean;
  attachments?: Attachment[];
  attachmentsLoading?: boolean;
  attachmentsError?: string | null;
}

function formatDateLabel(dateStr: string): string {
  if (!dateStr) return '';
  return format(parseISO(dateStr), "d 'de' MMM. 'de' yyyy", { locale: ptBR });
}

const isEsquecimento = (type: string) => type === 'Esquecimento';

export function PleaFormSheet({
  open,
  onClose,
  title,
  form,
  onChange,
  onSubmit,
  submitting,
  attachments,
  attachmentsLoading = false,
  attachmentsError = null,
}: PleaFormSheetProps) {
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const esquecimento = isEsquecimento(form.type);

  const submitDisabled =
    submitting ||
    !form.type ||
    !form.description ||
    !form.start ||
    (esquecimento && !form.startTime);

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[90dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 px-4 pb-6 pt-4">
          <div>
            <label htmlFor="plea-type" className="mb-1 block text-xs font-medium text-slate-500">Tipo</label>
            <select
              id="plea-type"
              value={form.type}
              onChange={(e) => onChange((f) => ({ ...f, type: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              required
            >
              <option value="">Selecione...</option>
              {PLEA_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="plea-desc" className="mb-1 block text-xs font-medium text-slate-500">
              Descrição <span className="text-red-500">*</span>
            </label>
            <textarea
              id="plea-desc"
              value={form.description}
              onChange={(e) => onChange((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">
                Início {esquecimento && <span className="text-red-500">*</span>}
              </p>
              <button
                type="button"
                onClick={() => setStartPickerOpen(true)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors hover:border-slate-300 focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                aria-label="Selecionar data de início"
              >
                <span className={form.start ? 'text-slate-900' : 'text-slate-400'}>
                  {form.start ? formatDateLabel(form.start) : 'Selecionar'}
                </span>
                <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              </button>
            </div>
            <div>
              <label htmlFor="plea-start-time" className="mb-1 block text-xs font-medium text-slate-500">
                Hora início
              </label>
              <div className="relative flex items-center rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-400">
                <Clock className="mr-2 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <input
                  id="plea-start-time"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => onChange((f) => ({ ...f, startTime: e.target.value }))}
                  className="flex-1 bg-transparent py-2.5 text-sm text-slate-900 outline-none"
                />
              </div>
            </div>
          </div>

          {!esquecimento && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500">Fim</p>
                <button
                  type="button"
                  onClick={() => setEndPickerOpen(true)}
                  className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors hover:border-slate-300 focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                  aria-label="Selecionar data de fim"
                >
                  <span className={form.end ? 'text-slate-900' : 'text-slate-400'}>
                    {form.end ? formatDateLabel(form.end) : 'Selecionar'}
                  </span>
                  <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                </button>
              </div>
              <div>
                <label htmlFor="plea-end-time" className="mb-1 block text-xs font-medium text-slate-500">
                  Hora fim
                </label>
                <div className="relative flex items-center rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-400">
                  <Clock className="mr-2 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  <input
                    id="plea-end-time"
                    type="time"
                    value={form.endTime}
                    onChange={(e) => onChange((f) => ({ ...f, endTime: e.target.value }))}
                    className="flex-1 bg-transparent py-2.5 text-sm text-slate-900 outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {attachments !== undefined && (
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Anexos enviados</p>
              <AttachmentList
                attachments={attachments}
                attachmentsLoading={attachmentsLoading}
                attachmentsError={attachmentsError}
              />
            </div>
          )}

          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors hover:bg-slate-100">
            <Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" />
            {form.file ? form.file.name : 'Anexar documento'}
            <input
              type="file"
              className="hidden"
              onChange={(e) => onChange((f) => ({ ...f, file: e.target.files?.[0] ?? null }))}
            />
          </label>

          <Separator />

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button className="flex-1 gap-2" onClick={onSubmit} disabled={submitDisabled}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <DatePickerDialog
      open={startPickerOpen}
      onClose={() => setStartPickerOpen(false)}
      value={form.start}
      onChange={(date) => onChange((f) => ({ ...f, start: date }))}
      label="Selecionar data de início"
    />
    <DatePickerDialog
      open={endPickerOpen}
      onClose={() => setEndPickerOpen(false)}
      value={form.end}
      onChange={(date) => onChange((f) => ({ ...f, end: date }))}
      label="Selecionar data de fim"
    />
    </>
  );
}
